import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';

import { AuthScreen, SubmitButton } from '@/components/AuthScreen';
import { Field } from '@/components/Field';
import { useLogin, useResendVerification } from '@/hooks/useSession';
import { useT } from '@/i18n/context';
import { ApiError, isUnverifiedEmail } from '@/lib/api';

/**
 * Where the confirmation link lands, by outcome.
 *
 * The API redirects here rather than answering the mail client with JSON, and
 * says what happened in the query string. Two of the three are good news.
 */
const CONFIRMED = {
  ok: 'auth.verified_ok',
  already: 'auth.verified_already',
} as const;

export function LoginPage() {
  const navigate = useNavigate();
  const t = useT();
  const [params] = useSearchParams();

  // Handed over by the sign-up screen, which redirects here the moment the
  // account exists. Deliberately not in the URL — see RegisterPage. It does not
  // survive a reload, which is the right lifetime for a one-off notice.
  const { state } = useLocation();
  const registered = (state as { registered?: string } | null)?.registered ?? null;

  // They typed it a second ago; making them type it again would be rude.
  const [email, setEmail] = useState(registered ?? '');
  const [password, setPassword] = useState('');

  const signIn = useLogin();
  const resend = useResendVerification();
  const failure = signIn.error instanceof ApiError ? signIn.error : null;

  // The API answers a wrong password with a validation error on `email`, kept
  // identical to the unknown-account response so it cannot be used to probe
  // which addresses are registered. Showing it at the top rather than under the
  // email field avoids implying the address itself was the problem.
  const fieldErrors = failure?.status === 422 ? {} : (failure?.fieldErrors ?? {});

  const outcome = params.get('verified');
  const expired = outcome === 'invalid' ? t('auth.verified_invalid') : null;
  const confirmed =
    outcome === 'ok' || outcome === 'already' ? t(CONFIRMED[outcome]) : null;

  const justRegistered = registered
    ? `${t('auth.check_email', { email: registered })} ${t('auth.check_email_hint')}`
    : null;

  const summary = failure ? failure.message : expired;
  // The server's own sentence takes precedence, already in the language this
  // request asked for; the rest are this screen's own.
  const notice = resend.isSuccess ? resend.data.message : (justRegistered ?? confirmed);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    signIn.mutate(
      { email, password },
      { onSuccess: () => void navigate('/', { replace: true }) },
    );
  };

  return (
    <AuthScreen
      title={t('auth.login_title')}
      subtitle={t('auth.login_subtitle')}
      error={summary}
      notice={notice}
      footer={
        <div className="flex justify-between">
          <Link to="/register" className="underline">
            {t('auth.create_account_link')}
          </Link>
          <Link to="/forgot-password" className="underline">
            {t('auth.forgot_link')}
          </Link>
        </div>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field
          label={t('auth.email')}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          error={fieldErrors.email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Field
          label={t('auth.password')}
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          error={fieldErrors.password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <SubmitButton pending={signIn.isPending}>
          {signIn.isPending ? t('auth.signing_in') : t('auth.sign_in')}
        </SubmitButton>

        {/* Only for the one failure a button can actually fix. The password was
            right, so the address is this account's and asking for a new link
            gives away nothing; every other failure leaves nothing to offer. */}
        {isUnverifiedEmail(signIn.error) && (
          <button
            type="button"
            disabled={resend.isPending}
            onClick={() => resend.mutate(email)}
            className="text-sm underline disabled:opacity-40"
          >
            {resend.isPending ? t('auth.sending') : t('auth.resend_verification')}
          </button>
        )}
      </form>
    </AuthScreen>
  );
}
